import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Grid,
  Paper,
  InputAdornment,
  FormGroup,
  FormControlLabel,
  Switch,
  Alert
} from '@mui/material';

// Icons
import PersonIcon from '@mui/icons-material/Person';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

// Custom components
import StockSearchBar from '../../../common/StockSearchBar';
import CongressSearchBar from '../../../common/CongressSearchBar';

const CongressionalTradeConfig = ({ alertData, onUpdateAlertData }) => {
  const mainFilters = alertData.mainFilters || [];
  const conditions = alertData.conditions || {};

  // State for conditional mode
  const [conditionalMode, setConditionalMode] = useState(
    conditions.symbols && conditions.symbols.length > 0
  );

  // Extract representatives and symbols
  const representatives = mainFilters
    .filter(filter => filter.type === 'representative')
    .map(filter => filter.value);

  // Get symbols from either mainFilters or conditions based on mode
  const symbols = conditionalMode
    ? (conditions.symbols || [])
    : mainFilters
      .filter(filter => filter.type === 'symbol')
      .map(filter => filter.value);

  // Effect to update data structure when conditional mode changes
  useEffect(() => {
    if (conditionalMode) {
      // Move symbols from mainFilters to conditions
      const symbolsFromMainFilters = mainFilters
        .filter(filter => filter.type === 'symbol')
        .map(filter => filter.value);

      // Only update if there are symbols to move or if we need to preserve existing condition symbols
      if (symbolsFromMainFilters.length > 0 || conditions.symbols) {
        const updatedMainFilters = mainFilters.filter(filter => filter.type !== 'symbol');
        const updatedConditions = {
          ...conditions,
          symbols: symbolsFromMainFilters.length > 0 ? symbolsFromMainFilters : (conditions.symbols || [])
        };

        onUpdateAlertData({
          mainFilters: updatedMainFilters,
          conditions: updatedConditions
        });
      }
    } else {
      // Move symbols from conditions to mainFilters
      const symbolsFromConditions = conditions.symbols || [];

      // Only update if there are symbols to move
      if (symbolsFromConditions.length > 0) {
        const symbolFilters = symbolsFromConditions.map(symbol => ({
          type: 'symbol',
          value: symbol
        }));

        const updatedMainFilters = [
          ...mainFilters.filter(filter => filter.type !== 'symbol'),
          ...symbolFilters
        ];

        const { symbols, ...remainingConditions } = conditions;

        onUpdateAlertData({
          mainFilters: updatedMainFilters,
          conditions: remainingConditions
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conditionalMode]);

  // Handle adding a representative
  const handleRepresentativeSelect = (representative, resultData) => {
    if (!representative) return;

    // For single representative, replace any existing one
    const updatedMainFilters = [
      ...mainFilters.filter(filter => filter.type !== 'representative'),
      { type: 'representative', value: representative }
    ];

    onUpdateAlertData({ mainFilters: updatedMainFilters });
  };

  // Handle removing a representative
  const handleRemoveRepresentative = (repToRemove) => {
    const updatedMainFilters = mainFilters.filter(
      filter => !(filter.type === 'representative' && filter.value === repToRemove)
    );
    onUpdateAlertData({ mainFilters: updatedMainFilters });
  };

  // Handle selecting a stock symbol
  const handleSymbolSelect = (symbol, resultData) => {
    if (!symbol || symbols.includes(symbol)) return;

    if (conditionalMode) {
      // Add to conditions.symbols array
      const updatedConditions = {
        ...conditions,
        symbols: [...(conditions.symbols || []), symbol]
      };

      onUpdateAlertData({ conditions: updatedConditions });
    } else {
      // Add to mainFilters
      const updatedMainFilters = [
        ...mainFilters,
        { type: 'symbol', value: symbol }
      ];

      onUpdateAlertData({ mainFilters: updatedMainFilters });
    }
  };

  // Handle removing a symbol
  const handleRemoveSymbol = (symbolToRemove) => {
    if (conditionalMode) {
      // Remove from conditions.symbols array
      const updatedConditions = {
        ...conditions,
        symbols: (conditions.symbols || []).filter(s => s !== symbolToRemove)
      };

      onUpdateAlertData({ conditions: updatedConditions });
    } else {
      // Remove from mainFilters
      const updatedMainFilters = mainFilters.filter(
        filter => !(filter.type === 'symbol' && filter.value === symbolToRemove)
      );

      onUpdateAlertData({ mainFilters: updatedMainFilters });
    }
  };

  // Handle minimum amount change
  const handleMinAmountChange = (e) => {
    onUpdateAlertData({
      conditions: {
        ...conditions,
        minAmount: e.target.value
      }
    });
  };

  // Handle trade type change
  const handleTradeTypeChange = (e) => {
    onUpdateAlertData({
      conditions: {
        ...conditions,
        tradeType: e.target.value
      }
    });
  };

  // Toggle conditional mode
  const handleConditionalModeToggle = (e) => {
    setConditionalMode(e.target.checked);
  };

  return (
    <Box sx={{ py: 2 }}>
      <Typography variant="h6" gutterBottom>
        Select a Representative to Monitor
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, mb: 4 }}>
        <Box sx={{ mb: 2 }}>
          <CongressSearchBar
            onRepresentativeSelect={handleRepresentativeSelect}
            currentRepresentatives={representatives}
            maxRepresentatives={1}
            placeholder="Search for a representative (e.g., 'Nancy Pelosi')"
            disabled={representatives.length > 0}
            hideSearch={representatives.length > 0}
          />
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {representatives.length > 0 ? (
            representatives.map((rep) => (
              <Chip
                key={rep}
                label={rep}
                onDelete={() => handleRemoveRepresentative(rep)}
                color="primary"
                variant="outlined"
                icon={<PersonIcon />}
              />
            ))
          ) : (
            <Typography variant="body2" color="text.secondary">
              No representative selected. Add a representative to monitor their trading activity.
            </Typography>
          )}
        </Box>
      </Paper>

      <Typography variant="h6" gutterBottom>
        Select Stocks to Monitor {conditionalMode ? '(Representative Buys)' : ''}
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, mb: 4 }}>
        <Box sx={{ mb: 2 }}>
          <StockSearchBar
            onSymbolSelect={handleSymbolSelect}
            currentSymbols={symbols}
            maxSymbols={Infinity}
            placeholder="Search for stocks by symbol"
          />
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
          {symbols.length > 0 ? (
            symbols.map((symbol) => (
              <Chip
                key={symbol}
                label={symbol}
                onDelete={() => handleRemoveSymbol(symbol)}
                color="primary"
                variant="outlined"
                icon={<ShowChartIcon />}
              />
            ))
          ) : (
            <Typography variant="body2" color="text.secondary">
              No stocks selected. Add stocks to monitor for trading activity.
            </Typography>
          )}
        </Box>

        {representatives.length > 0 && (
          <Box sx={{ mt: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2 }}>
            <FormGroup>
              <FormControlLabel
                control={
                  <Switch
                    checked={conditionalMode}
                    onChange={handleConditionalModeToggle}
                    color="primary"
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant="body1">
                      Enable specific representative-stock pairs
                    </Typography>
                    <InfoOutlinedIcon
                      fontSize="small"
                      color="action"
                      sx={{ ml: 1 }}
                    />
                  </Box>
                }
              />
            </FormGroup>

            <Alert severity="info" sx={{ mt: 2 }}>
              {conditionalMode ? (
                <Typography variant="body2">
                  When enabled, stocks are stored in conditions and you'll only receive alerts when {representatives[0]} trades the specific stocks you selected. For example, alert me when {representatives[0]} trades PLTR.
                </Typography>
              ) : (
                <Typography variant="body2">
                  Currently, stocks are stored as main filters and you'll receive alerts when {representatives[0]} trades OR when any representative trades any of the selected stocks.
                </Typography>
              )}
            </Alert>
          </Box>
        )}
      </Paper>

      <Typography variant="h6" gutterBottom>
        3. Additional Filters (Optional)
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, mb: 4 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Minimum Transaction Amount"
              type="number"
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
              fullWidth
              value={conditions.minAmount || ''}
              onChange={handleMinAmountChange}
              helperText="Only notify for trades above this amount"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel id="trade-type-label">Trade Type</InputLabel>
              <Select
                labelId="trade-type-label"
                value={conditions.tradeType || ''}
                onChange={handleTradeTypeChange}
                label="Trade Type"
              >
                <MenuItem value="">All Trade Types</MenuItem>
                <MenuItem value="Purchase">Purchases Only</MenuItem>
                <MenuItem value="Sale">Sales Only</MenuItem>
              </Select>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                Filter by transaction type
              </Typography>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {(representatives.length === 0 && symbols.length === 0) && (
        <Typography variant="body2" color="error" sx={{ mt: 2 }}>
          Please select at least one representative or stock to monitor.
        </Typography>
      )}
    </Box>
  );
};

export default CongressionalTradeConfig;